import { Provider, useDispatch } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { store } from '../store';
import { queryClient } from '../lib/query-client';
import { appWithTranslation } from 'next-i18next';

import 'react-dates/initialize';
import '../styles/app.scss';
import 'react-dates/lib/css/_datepicker.css';
import { AuthProvider } from '@lb-classroom/feature-auth';
import ActionCableProvider from '../providers/ActionCableProvider';
import PhoenixSocketProvider from '../providers/PhoenixSocketProvider';
import { CookiesProvider } from 'react-cookie';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import Layout from '../components/layout';
import React, { useCallback, useEffect } from 'react';
import Script from 'next/script';
import { useRouter } from 'next/router';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import * as CurrentUserActions from '../actions/currentUser';
import { AppDispatch } from '../store';
import { Toaster } from '@lb-classroom/ui';
import Bugsnag, { isBugsnagEnabled } from '@lib/bugsnag';
import BugsnagUserSetter from '../components/BugsnagUserSetter';
import { GA_MEASUREMENT_ID, pageview } from '../lib/gtag';
import { tokenRefreshService } from '../utils/api/token-refresh.service';
import { configureApiClient } from '../api/client';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Configure the API client for generated SDK
configureApiClient();

// Type for pages with custom layouts
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

// AuthProvider wrapper that provides the fetchCurrentUser action via dispatch
const AuthProviderWithDispatch = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const fetchCurrentUser = useCallback(() => {
    dispatch(CurrentUserActions.fetchCurrentUser() as any);
  }, [dispatch]);
  const refreshAuthToken = useCallback(() => {
    return tokenRefreshService.refreshTokenIfNeeded();
  }, []);

  return (
    <AuthProvider
      fetchCurrentUser={fetchCurrentUser}
      refreshAuthToken={refreshAuthToken}
    >
      {children}
    </AuthProvider>
  );
};

// Conditionally wrap children with PhoenixSocketProvider only when WebRTC is enabled
const ConditionalPhoenixSocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  if (FEATURE_FLAGS.webrtcVideoCall) {
    return <PhoenixSocketProvider>{children}</PhoenixSocketProvider>;
  }
  return <>{children}</>;
};

// Conditionally wrap children with GoogleOAuthProvider only when Google OAuth is configured
const ConditionalGoogleOAuthProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  if (FEATURE_FLAGS.googleOAuth) {
    return (
      <GoogleOAuthProvider
        clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
      >
        {children}
      </GoogleOAuthProvider>
    );
  }
  return <>{children}</>;
};

// Create ErrorBoundary at module scope — falls back to Fragment when Bugsnag is disabled
const ErrorBoundary = isBugsnagEnabled
  ? Bugsnag.getPlugin('react')!.createErrorBoundary(React)
  : React.Fragment;

const ErrorFallback = () => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h1>Something went wrong</h1>
    <p>We have been notified and are looking into it.</p>
    <button onClick={() => window.location.reload()}>Reload page</button>
  </div>
);

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const router = useRouter();
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) {
      return;
    }
    const handleRouteChange = (url: string) => {
      pageview(url, router.locale);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    pageview(window.location.pathname + window.location.search, router.locale);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, router.locale]);

  const gaScripts =
    process.env.NODE_ENV === 'production' && GA_MEASUREMENT_ID ? (
      <>
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          strategy="afterInteractive"
        />
        <Script id="ga4-init" strategy="afterInteractive">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            window.gtag = window.gtag || gtag;
            gtag('js', new Date());
            gtag('config', '${GA_MEASUREMENT_ID}', {
              cookie_domain: 'lingualbox.com',
              send_page_view: false
            });
          `}
        </Script>
      </>
    ) : null;

  const errorBoundaryProps = isBugsnagEnabled
    ? { FallbackComponent: ErrorFallback }
    : {};

  // Use custom layout if the page defines one, otherwise use the default Layout
  const getLayout = Component.getLayout ?? ((page) => <Layout>{page}</Layout>);

  return (
    <ErrorBoundary {...errorBoundaryProps}>
      <ConditionalGoogleOAuthProvider>
        <CookiesProvider>
          <QueryClientProvider client={queryClient}>
            <Provider store={store}>
              <AuthProviderWithDispatch>
                <ActionCableProvider>
                  <ConditionalPhoenixSocketProvider>
                    <BugsnagUserSetter />
                    {gaScripts}
                    <Toaster position="top-right" richColors />
                    {getLayout(
                      <>
                        {process.env.NODE_ENV === 'production' && (
                          <Script id="dashly-script">
                            {`
                            !function(){function t(t,e){return function(){window.dashlyasync.push(t,arguments)}}if("undefined"==typeof dashly){var e=document.createElement("script");e.type="text/javascript",e.async=!0,e.src="https://cdn.dashly.app/api.min.js",document.getElementsByTagName("head")[0].appendChild(e),window.dashly={},window.dashlyasync=[],dashly.settings={};for(var n=["connect","track","identify","auth","onReady","addCallback","removeCallback","trackMessageInteraction"],a=0;a<n.length;a++)dashly[n[a]]=t(n[a])}}(),dashly.connect("6183-d9afcd0f70d4821f6cb52c53646");
                          `}
                          </Script>
                        )}
                        <Component {...pageProps} />
                      </>,
                    )}
                  </ConditionalPhoenixSocketProvider>
                </ActionCableProvider>
              </AuthProviderWithDispatch>
            </Provider>
            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
          </QueryClientProvider>
        </CookiesProvider>
      </ConditionalGoogleOAuthProvider>
    </ErrorBoundary>
  );
};

export default appWithTranslation(MyApp);
